import type { SVGProps } from 'react';

/**
 * Servwell v2 icon set — hand-drawn 24-viewBox strokes, currentColor, no icon lib.
 * Matches the approved core-screens prototype (PRD v2 §5). Add icons here only;
 * never inline raw <svg> in pages.
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base({ size = 18, ...props }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    ...props,
  } as const;
}

export const IconToday = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1" />
  </svg>
);

export const IconSchedule = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

/**
 * A panel van (107), and since 112 the only one of the two in the sidebar: `IconFleet`
 * replaced `IconMap` when Fleet absorbed Map (D5). It was drawn as a van rather than a
 * second pin because the two entries stood side by side for a while and had to read as
 * different ideas — "what do we drive" against "where is the work". They are one entry
 * now, and the van is the right half to have kept: the page is keyed on vehicles.
 */
export const IconFleet = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 6h10v10H3z" />
    <path d="M13 9h3.6l3.4 4v3h-7z" />
    <circle cx="7" cy="18" r="1.6" />
    <circle cx="17" cy="18" r="1.6" />
  </svg>
);

/**
 * A dropped pin — the shape everyone already reads as "where".
 *
 * ⚠️ NO nav entry uses this since 112 retired `/map`. Kept rather than deleted because it
 * is the natural icon for any future "where" surface and costs nothing but its own tree
 * shake; if a year passes with no consumer, delete it rather than inventing one.
 */
export const IconMap = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

export const IconPipeline = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="4" width="5" height="16" rx="1.5" />
    <rect x="10" y="4" width="5" height="10" rx="1.5" />
    <rect x="17" y="4" width="4" height="13" rx="1.5" />
  </svg>
);

export const IconRequests = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 13h5l2 3h4l2-3h5" />
    <path d="M5 6h14l2 7v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6l2-7Z" />
  </svg>
);

export const IconQuotes = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6 2h9l5 5v15H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z" />
    <path d="M14 2v6h6M9 13h6M9 17h6" />
  </svg>
);

export const IconJobs = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M14.7 6.3a4.5 4.5 0 0 0-6 5.6L3 17.6V21h3.4l5.7-5.7a4.5 4.5 0 0 0 5.6-6l-3 3-2.8-.7-.7-2.8 3.5-2.5Z" />
  </svg>
);

/** A ticket stub (ops insights 5a): a card with a notch each side and a tear line. */
export const IconTickets = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2.5a2.5 2.5 0 0 0 0 5V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2.5a2.5 2.5 0 0 0 0-5Z" />
    <path d="M15 5v2M15 11v2M15 17v2" />
  </svg>
);

/** The bell (ops insights 5a, ticket 05): a person's own notifications. */
export const IconBell = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6.5 2 6.5H4S6 14 6 9Z" />
    <path d="M10 19a2 2 0 0 0 4 0" />
  </svg>
);

/** Alerts (ops insights Phase 7): what the monitors found that needs someone. Not the bell. */
export const IconAlerts = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M10.3 4.2 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9.5v4.5M12 17.2v.1" />
  </svg>
);

export const IconProjects = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
    <path d="M8 13h8" />
  </svg>
);

export const IconMoney = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15 9.5c-.6-1-1.6-1.5-3-1.5-1.8 0-3 .9-3 2.2 0 2.9 6 1.5 6 4.3 0 1.3-1.2 2.2-3 2.2-1.4 0-2.4-.5-3-1.5M12 6.5v11" />
  </svg>
);

export const IconCustomers = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 20c.6-3.2 2.7-5 5.5-5s4.9 1.8 5.5 5" />
    <path d="M16 5.5a3 3 0 0 1 0 5.6M17.5 15.2c1.9.6 3 2.1 3.4 4.3" />
  </svg>
);

/** A price tag: Products & services, the price book's own page (CRM-18). */
export const IconProducts = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3.5 12.6V4.5a1 1 0 0 1 1-1h8.1a1 1 0 0 1 .7.3l7.4 7.4a1.6 1.6 0 0 1 0 2.3l-6.8 6.8a1.6 1.6 0 0 1-2.3 0l-7.4-7.4a1 1 0 0 1-.3-.7Z" />
    <circle cx="8.2" cy="8.2" r="1.4" />
  </svg>
);

export const IconInventory = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 8l9-4.5L21 8v8l-9 4.5L3 16V8Z" />
    <path d="M3 8l9 4.5L21 8M12 12.5V21" />
  </svg>
);

export const IconReports = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 20V10M10 20V4M16 20v-7M21 20H3" />
  </svg>
);

export const IconSettings = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.5l1.2 2.6 2.8-.5 1.4 2.4 2.6 1.2-.5 2.8 1.7 2.3-1.7 2.3.5 2.8-2.6 1.2-1.4 2.4-2.8-.5L12 21.5l-1.2-2.6-2.8.5-1.4-2.4-2.6-1.2.5-2.8L2.8 12l1.7-2.3-.5-2.8 2.6-1.2 1.4-2.4 2.8.5L12 2.5Z" />
  </svg>
);

export const IconSearch = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M20 20l-4.4-4.4" />
  </svg>
);

export const IconPlus = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconX = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const IconChevronLeft = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M14.5 5.5L8 12l6.5 6.5" />
  </svg>
);

export const IconChevronRight = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M9.5 5.5L16 12l-6.5 6.5" />
  </svg>
);

export const IconChevronUp = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M5.5 14.5L12 8l6.5 6.5" />
  </svg>
);

export const IconChevronDown = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M5.5 9.5L12 16l6.5-6.5" />
  </svg>
);

export const IconArrowRight = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 12h16M13 5l7 7-7 7" />
  </svg>
);

export const IconSort = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
  </svg>
);

export const IconSortAsc = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);

export const IconSortDesc = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 5v14M6 13l6 6 6-6" />
  </svg>
);

export const IconTheme = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3a9 9 0 0 0 0 18V3Z" fill="currentColor" stroke="none" />
  </svg>
);

export const IconLogout = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M9 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4M15 8l4 4-4 4M19 12H9" />
  </svg>
);

/** The phone bar's fourth slot (116). Three dots, not a hamburger: the hamburger meant
 *  "the whole nav" and this opens the REST of it — the drawer it opens still carries the
 *  hamburger's contents, but the bar in front of it already spends three of them. */
export const IconMore = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

export const IconWrenchBig = (p: IconProps) => <IconJobs {...p} size={p.size ?? 24} />;

export const IconTimer = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2.5 2M9 2h6" />
  </svg>
);

export const IconPlay = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
);

export const IconStop = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="6.5" y="6.5" width="11" height="11" rx="2" />
  </svg>
);

export const IconTrash = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6" />
  </svg>
);

export const IconCheck = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

export const IconChecklist = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 6l2 2 3-3M3 14l2 2 3-3M12 6h9M12 15h9" />
  </svg>
);

export const IconTeam = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
    <path d="M16 5.5a3.2 3.2 0 010 5.6M17.5 14.8c2.1.7 3.5 2.5 3.5 5.2" />
  </svg>
);

export const IconMail = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M3 7l8.3 5.6a1.3 1.3 0 001.4 0L21 7" />
  </svg>
);
